import type { Notification } from '@/types'
import { getPermissionForPath } from '@/config/routePermissions'
import {
  extractShiftAlertContext,
  isShiftAlertNotification,
} from './shiftAlertNotificationContext'

function normalizeAppPath(path: string): string {
  return path.split('?')[0]?.split('#')[0] ?? path
}

/** Predicate matching `usePermissions().can` — true if the user holds module+action. */
export type CanFn = (module: string, action: string) => boolean

/**
 * When a notification's natural destination is a team/admin screen the user can't reach,
 * degrade to the self-service equivalent. Keyed by the permission *module* that gated the
 * route (from getPermissionForPath), so one entry covers every path variant of that module
 * (e.g. `/shifts`, `/shifts?...`, `/companies/:id/shifts` all map via module `shifts`).
 */
const SELF_FALLBACKS: Record<string, string> = {
  shifts: '/my-shifts',
  leave: '/my-leaves',
  payroll: '/payroll/me',
  attendance: '/my-attendance',
  time_corrections: '/my-time-corrections',
  bonuses: '/my-bonuses',
  security_ledger: '/my-deposits',
  documents: '/my-documents',
  expenses: '/my-expenses',
}

/**
 * The single access-aware navigation layer. Returns `path` unchanged if the user can access
 * it; otherwise the self-service equivalent, or `/notifications` (a safe no-op) when no self
 * route exists. Query params survive when the path is accessible, so deep links keep working.
 */
export function toAccessiblePath(path: string, can: CanFn): string {
  const perm = getPermissionForPath(normalizeAppPath(path))
  if (!perm) return path // self/public route — no gate

  const ok = perm.actions
    ? perm.actions.some((a) => can(perm.module, a))
    : can(perm.module, perm.action ?? '')
  if (ok) return path

  const fallback = SELF_FALLBACKS[perm.module]
  if (!fallback) return '/notifications'

  // Carry the original query/hash (e.g. shiftAssignmentId, companyId) onto the self route so
  // the destination can still deep-link to the specific record.
  const search = path.slice(normalizeAppPath(path).length)
  return `${fallback}${search}`
}

export function resolveInAppPath(url: string): string | undefined {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const u = new URL(url, base)
    if (typeof window !== 'undefined' && u.origin !== window.location.origin) return undefined
    return `${u.pathname}${u.search}${u.hash}`
  } catch {
    return undefined
  }
}

function resolveNotificationReferencePath(n: Notification): string | undefined {
  const refType = n.reference?.type
  const refId = n.reference?.id

  switch (refType) {
    case 'announcement':
      return '/announcements'
    case 'leave':
      return '/my-leaves'
    case 'bonus':
      return '/my-bonuses'
    case 'penalty':
      return '/penalties'
    case 'security_ledger_tx':
      return '/my-deposits'
    case 'payroll_batch':
      return refId ? `/payroll/batches/${encodeURIComponent(refId)}` : '/payroll'
    case 'payroll_run':
      return '/payroll'
    case 'expense_claim':
      // Approvers are told a claim awaits them; claimants hear about their own claim.
      return n.type === 'EXPENSE_CLAIM_SUBMITTED' ? '/expense-approvals' : '/my-expenses'
    default:
      return undefined
  }
}

/** Natural destination for a notification, before any access check. */
function resolveRawNotificationPath(n: Notification): string | undefined {
  const shiftPath = isShiftAlertNotification(n)
    ? extractShiftAlertContext(n).shiftsPlanningPath
    : undefined
  if (shiftPath) return shiftPath

  const inAppPath = n.reference?.url ? resolveInAppPath(n.reference.url) : undefined
  const fallbackPath = resolveNotificationReferencePath(n)

  if (!inAppPath) return fallbackPath

  const normalizedPath = normalizeAppPath(inAppPath)
  if (normalizedPath === '/notifications' && fallbackPath) return fallbackPath

  return inAppPath
}

export interface ResolveNotificationPathOptions {
  /**
   * Permission predicate (`usePermissions().can`). When provided, the destination is run
   * through {@link toAccessiblePath} so users who can't reach the team/admin screen are sent
   * to the self-service equivalent. Omit to get the raw, un-degraded path.
   */
  can?: CanFn
}

export function resolveNotificationInternalPath(
  n: Notification,
  options: ResolveNotificationPathOptions = {}
): string | undefined {
  const raw = resolveRawNotificationPath(n)
  if (!raw) return undefined
  return options.can ? toAccessiblePath(raw, options.can) : raw
}
